'use client';

import { c as _c } from "react/compiler-runtime";
import { useActiveWallet, useAddToWatchlist, useGetProposalField, useRemoveFromWatchlist } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { HowItWorksInfoButton } from '@/components/how-it-works/how-it-works-info-button';
import { WhatIsAProposalModal } from '@/components/how-it-works/what-is-a-proposal-modal';
import { OptimizedImage } from '@/components/images/optimized-image';
import { ProposalShareButton } from '@/components/launchpad/proposal-share-button';
import { useProposalVoteModel } from '@/components/launchpad/use-proposal-vote-model';
import { ThumbWithOrigin } from '@/components/market/identity/identity-marks';
import type { MarketIdentityData } from '@/components/market/identity/market-identity';
import { ReactionVoteButtons } from '@/components/shared/reaction-vote-buttons';
import { AnimatedBookmark } from '@/components/ui/animated-bookmark';
import { resolveProposalCategory } from '@/config/proposal-display';
import { TOAST_IDS } from '@/config/toast-ids';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { useAuthenticatedWatchlist } from '@/hooks/use-authenticated-watchlist';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { trackEvent } from '@/lib/analytics';
import { showToast } from '@/lib/ui/toast';
import { cn } from '@/lib/ui/utils';
interface ProposalHeaderProps {
  proposal: {
    pubkey: string;
    title: string;
    description: string | null;
    coverImageUrl: string | null;
    /**
     * NSFW flag for `coverImageUrl` (spec 071). When true the cover is
     * rendered through the NSFW-blur overlay until the viewer taps to
     * reveal. Optional — defaults to `false` at the callsite.
     */
    coverImageNsfw?: boolean;
    category: string;
    /** Influencer / Featured origin mark on the cover's corner. */
    marketType?: MarketIdentityData['marketType'];
  };
  className?: string;
}
const FALLBACK_EMOJI = '\u{1F4A1}'; // 💡

/**
 * Proposal detail page header — Figma 3317:1388 top half. An 88px square
 * thumbnail (88px, `rounded-control` / 12px corners — cover image when present, otherwise a category-emoji fallback),
 * a top-right action row (Bookmark · Share · Vote), then the title and the
 * full description below. The Panel wrapper, status banner, and right-sidebar
 * identity card are owned by `proposal-detail-page.tsx`.
 */
export function ProposalHeader(t0) {
  const $ = _c(75);
  const {
    proposal,
    className
  } = t0;
  const t = useTranslations("launchpad.detail");
  const tWatch = useTranslations("common.watchlist");
  const proposalVotingEnabled = useFeatureGate("proposal_voting") === true;
  const {
    activeWallet
  } = useActiveWallet();
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  let t1;
  if ($[0] !== activeWallet) {
    t1 = activeWallet?.address !== undefined ? {
      viewerWallet: activeWallet.address
    } : {};
    $[0] = activeWallet;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== proposal.pubkey || $[3] !== t1) {
    t2 = {
      pubkey: proposal.pubkey,
      ...t1
    };
    $[2] = proposal.pubkey;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const viewerArgs = t2;
  const liveUpvotes = useGetProposalField(viewerArgs, "upvotes");
  const liveDownvotes = useGetProposalField(viewerArgs, "downvotes");
  const liveMyVote = useGetProposalField(viewerArgs, "myVote");
  const t3 = liveUpvotes ?? 0;
  const t4 = liveDownvotes ?? 0;
  const t5 = (liveMyVote ?? null) as 'up' | 'down' | null;
  let t6;
  if ($[5] !== t3 || $[6] !== t4 || $[7] !== t5) {
    t6 = {
      upvotes: t3,
      downvotes: t4,
      myVote: t5
    };
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const voteModel = useProposalVoteModel(proposal.pubkey, t6);
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      kind: "proposals",
      fields: ["pubkeys"]
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const {
    data: watchlist
  } = useAuthenticatedWatchlist(t7);
  const {
    ensureAuthenticated
  } = useAuthGate();
  const addToWatchlist = useAddToWatchlist();
  const removeFromWatchlist = useRemoveFromWatchlist();
  let t8;
  if ($[10] !== proposal.pubkey || $[11] !== watchlist?.pubkeys) {
    t8 = watchlist?.pubkeys?.includes(proposal.pubkey) ?? false;
    $[10] = proposal.pubkey;
    $[11] = watchlist?.pubkeys;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const isSaved = t8;
  let t9;
  if ($[13] !== addToWatchlist || $[14] !== ensureAuthenticated || $[15] !== isSaved || $[16] !== proposal.pubkey || $[17] !== removeFromWatchlist || $[18] !== tWatch) {
    t9 = function handleBookmarkToggle() {
      if (!ensureAuthenticated()) {
        return;
      }
      if (isSaved) {
        removeFromWatchlist.mutate({
          kind: "proposals",
          pubkey: proposal.pubkey
        });
      } else {
        addToWatchlist.mutate({
          kind: "proposals",
          pubkey: proposal.pubkey
        });
      }
      showToast({
        id: isSaved ? TOAST_IDS.WATCHLIST_REMOVED : TOAST_IDS.WATCHLIST_SAVED,
        message: isSaved ? tWatch("removed") : tWatch("added"),
        variant: "success"
      });
      trackEvent("proposal_bookmark_toggled", {
        proposalPubkey: proposal.pubkey,
        saved: !isSaved
      });
    };
    $[13] = addToWatchlist;
    $[14] = ensureAuthenticated;
    $[15] = isSaved;
    $[16] = proposal.pubkey;
    $[17] = removeFromWatchlist;
    $[18] = tWatch;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const handleBookmarkToggle = t9;
  const hasCover = proposal.coverImageUrl !== null && proposal.coverImageUrl.length > 0;
  const fallbackEmoji = resolveProposalCategory(proposal.category)?.emoji ?? FALLBACK_EMOJI;
  const description = proposal.description ?? "";
  let t10;
  if ($[20] !== className) {
    t10 = cn("flex flex-col gap-6", className);
    $[20] = className;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== fallbackEmoji || $[23] !== hasCover || $[24] !== proposal.category || $[25] !== proposal.coverImageNsfw || $[26] !== proposal.coverImageUrl || $[27] !== proposal.pubkey || $[28] !== proposal.title) {
    t11 = hasCover ? <OptimizedImage variant="thumbnail" entity={{
      kind: "market",
      pubkey: proposal.pubkey,
      category: String(proposal.category ?? "").toLowerCase(),
      coverImageUrl: proposal.coverImageUrl,
      nsfw: proposal.coverImageNsfw ?? false
    }} alt={proposal.title} size="size-22" rounded="rounded-control" priority={true} /> : <div aria-hidden="true" className="flex size-22 shrink-0 items-center justify-center rounded-control bg-background-tertiary text-5xl leading-none"><span>{fallbackEmoji}</span></div>;
    $[22] = fallbackEmoji;
    $[23] = hasCover;
    $[24] = proposal.category;
    $[25] = proposal.coverImageNsfw;
    $[26] = proposal.coverImageUrl;
    $[27] = proposal.pubkey;
    $[28] = proposal.title;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== proposal.marketType || $[31] !== t11) {
    t12 = <ThumbWithOrigin marketType={proposal.marketType}>{t11}</ThumbWithOrigin>;
    $[30] = proposal.marketType;
    $[31] = t11;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== proposal.title) {
    t13 = <h1 className="min-w-0 flex-1 text-heading text-foreground-primary @min-[1024px]/proposal-detail:hidden">{proposal.title}</h1>;
    $[33] = proposal.title;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => setHowItWorksOpen(true);
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t) {
    t15 = t("howProposalsWorkLabel");
    $[36] = t;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t15) {
    t16 = <HowItWorksInfoButton onClick={t14} ariaLabel={t15} className="bg-background-tertiary" />;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== t) {
    t17 = t("bookmarkLabel");
    $[40] = t;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== handleBookmarkToggle || $[43] !== isSaved || $[44] !== t17) {
    t18 = <AnimatedBookmark isSaved={isSaved} onClick={handleBookmarkToggle} ariaLabel={t17} className="bg-background-tertiary" />;
    $[42] = handleBookmarkToggle;
    $[43] = isSaved;
    $[44] = t17;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== proposal.pubkey || $[47] !== proposal.title) {
    t19 = <ProposalShareButton proposalPubkey={proposal.pubkey} proposalTitle={proposal.title} />;
    $[46] = proposal.pubkey;
    $[47] = proposal.title;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== proposalVotingEnabled || $[50] !== voteModel) {
    t20 = proposalVotingEnabled ? <ReactionVoteButtons reaction={voteModel.reaction} upCount={voteModel.upCount} downCount={voteModel.downCount} upAriaLabel={voteModel.upAriaLabel} downAriaLabel={voteModel.downAriaLabel} onReact={voteModel.onReact} className="rounded-control bg-background-tertiary" /> : null;
    $[49] = proposalVotingEnabled;
    $[50] = voteModel;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== t16 || $[53] !== t18 || $[54] !== t19 || $[55] !== t20) {
    t21 = <div className="flex w-full shrink-0 items-center justify-end gap-2 @min-[768px]/proposal-detail:w-auto">{t16}{t18}{t19}{t20}</div>;
    $[52] = t16;
    $[53] = t18;
    $[54] = t19;
    $[55] = t20;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== t12 || $[58] !== t13 || $[59] !== t21) {
    t22 = <div className="flex flex-wrap items-center justify-between gap-3 @min-[768px]/proposal-detail:flex-nowrap @min-[768px]/proposal-detail:items-start">{t12}{t13}{t21}</div>;
    $[57] = t12;
    $[58] = t13;
    $[59] = t21;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  let t23;
  if ($[61] !== proposal.title) {
    t23 = <h1 className="hidden text-heading text-foreground-primary @min-[1024px]/proposal-detail:block">{proposal.title}</h1>;
    $[61] = proposal.title;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  let t24;
  if ($[63] !== description) {
    t24 = description.length > 0 ? <p className="text-body text-foreground-tertiary whitespace-pre-wrap break-words">{description}</p> : null;
    $[63] = description;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  let t25;
  if ($[65] !== t23 || $[66] !== t24) {
    t25 = <div className="flex min-w-0 flex-col gap-1">{t23}{t24}</div>;
    $[65] = t23;
    $[66] = t24;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  let t26;
  if ($[68] !== howItWorksOpen) {
    t26 = <WhatIsAProposalModal open={howItWorksOpen} onOpenChange={setHowItWorksOpen} />;
    $[68] = howItWorksOpen;
    $[69] = t26;
  } else {
    t26 = $[69];
  }
  let t27;
  if ($[70] !== t10 || $[71] !== t22 || $[72] !== t25 || $[73] !== t26) {
    t27 = <div className={t10}>{t22}{t25}{t26}</div>;
    $[70] = t10;
    $[71] = t22;
    $[72] = t25;
    $[73] = t26;
    $[74] = t27;
  } else {
    t27 = $[74];
  }
  return t27;
}